import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import colors from '../../styles/colors';

const SUPPORT_EMAIL = 'jeff@visibilitylabs.ai';

const FIELD_ORDER = [
  'Company Overview',
  'Core Offering',
  'Unique Selling Points',
  'Target Audience',
  'Writing Guidelines',
];

const INSTRUCTIONS = `Please review the information we found about your company and make any necessary edits. We'll be referencing this throughout the AI SEO campaign to ensure any strategies and recommendations are fully aligned with your brand.\n\nThen once the information is approved, you can click the "Approve & Submit" button at the bottom of the page.`;

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: colors.bg.offWhite,
  },
  header: {
    background: colors.bg.white,
    padding: '16px 0',
    borderBottom: `1px solid ${colors.border.lightNavy}`,
  },
  headerInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '24px',
  },
  logoImg: {
    height: '34px',
    width: 'auto',
  },
  main: { flex: 1, padding: '48px 0' },
  mainInner: { maxWidth: '900px', margin: '0 auto', padding: '0 24px' },
  title: {
    fontSize: '28px',
    fontWeight: '800',
    color: colors.text.darkNavy,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: colors.text.muted,
    marginBottom: '24px',
    lineHeight: '1.6',
  },
  card: {
    background: colors.bg.white,
    border: `1px solid ${colors.border.lightNavy}`,
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  row: { marginBottom: '18px' },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '700',
    color: colors.text.darkNavy,
    marginBottom: '8px',
  },
  textarea: {
    width: '100%',
    minHeight: '240px',
    resize: 'vertical',
    borderRadius: '10px',
    border: `1px solid ${colors.border.light}`,
    padding: '12px 14px',
    fontSize: '14px',
    fontFamily: 'inherit',
    fontWeight: 400,
    outline: 'none',
    lineHeight: '1.6',
  },
  actions: {
    marginTop: '20px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  button: {
    background: colors.bg.primaryPurple,
    color: colors.text.white,
    border: 'none',
    borderRadius: '10px',
    padding: '12px 18px',
    fontSize: '14px',
    fontWeight: '700',
  },
  buttonSecondary: {
    background: colors.bg.white,
    color: colors.text.darkNavy,
    border: `1px solid ${colors.border.light}`,
    borderRadius: '10px',
    padding: '12px 18px',
    fontSize: '14px',
    fontWeight: '700',
    textDecoration: 'none',
    display: 'inline-block',
  },
  lockedBanner: {
    background: 'rgba(22, 163, 74, 0.08)',
    border: '1px solid rgba(22, 163, 74, 0.25)',
    color: '#14532d',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '18px',
    fontSize: '14px',
    fontWeight: '700',
  },
  errorBanner: {
    background: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.25)',
    color: '#7f1d1d',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '18px',
    fontSize: '14px',
    fontWeight: '700',
  },
  footer: {
    background: colors.bg.darkNavy,
    padding: '22px 0',
    textAlign: 'center',
  },
  footerText: { fontSize: '13px', color: 'rgba(255,255,255,0.5)' },

  thankYouWrap: {
    background: `linear-gradient(135deg, ${colors.bg.primaryPurple} 0%, ${colors.bg.darkNavy} 100%)`,
    borderRadius: '16px',
    padding: '28px',
    marginBottom: '24px',
    color: colors.text.white,
    textAlign: 'center',
    boxShadow: '0 12px 30px rgba(23, 15, 73, 0.18)',
  },
  thankYouIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '18px',
    background: 'rgba(255, 255, 255, 0.14)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    margin: '0 auto 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '30px',
  },
  thankYouTitle: {
    fontSize: '28px',
    fontWeight: '800',
    marginBottom: '10px',
    letterSpacing: '-0.5px',
  },
  thankYouText: {
    maxWidth: '560px',
    margin: '0 auto',
    fontSize: '15px',
    lineHeight: '1.7',
    color: 'rgba(255,255,255,0.82)',
  },
  thankYouMeta: {
    marginTop: '18px',
    fontSize: '13px',
    color: 'rgba(255,255,255,0.75)',
  },
  thankYouActions: {
    marginTop: '26px',
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  thankYouBtnPrimary: {
    background: colors.bg.white,
    color: colors.text.accentBlue,
    border: 'none',
    borderRadius: '10px',
    padding: '12px 18px',
    fontSize: '14px',
    fontWeight: '800',
    textDecoration: 'none',
    display: 'inline-block',
  },
  thankYouBtnSecondary: {
    background: 'transparent',
    color: colors.text.white,
    border: '1px solid rgba(255,255,255,0.35)',
    borderRadius: '10px',
    padding: '12px 18px',
    fontSize: '14px',
    fontWeight: '800',
    textDecoration: 'none',
    display: 'inline-block',
  },
};

function InitialOnboardingDataApproval() {
  const { uuid } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [locked, setLocked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [session, setSession] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);

  const [formData, setFormData] = useState({});
  const [feedback, setFeedback] = useState('');

  const detailUrl = useMemo(
    () => `/forms/initial-onboarding-data-approval/${uuid}/`,
    [uuid]
  );

  useEffect(() => {
    setLoading(true);
    setError('');
    setNotFound(false);

    api
      .get(detailUrl)
      .then((res) => {
        setSession(res.data);
        const isLocked = Boolean(res.data?.is_locked);
        setLocked(isLocked);
        const raw = res.data?.working_data || {};
        // Replace literal \\n with real line breaks for display/editing
        const normalized = Object.fromEntries(
          Object.entries(raw).map(([k, v]) => [
            k,
            typeof v === 'string' ? v.replaceAll('\\n', '\n') : v,
          ])
        );
        setFormData(normalized);
        setFeedback(res.data?.feedback || '');
        setShowThankYou(isLocked);
        setLoading(false);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.detail || 'This form link is invalid.';
        setError(msg);
        setNotFound(err?.response?.status === 404);
        setLoading(false);
      });
  }, [detailUrl]);

  const onChangeField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (locked || submitting) return;

    setSubmitting(true);
    setError('');

    api
      .post(`${detailUrl}submit/`, { working_data: formData, feedback })
      .then(() => {
        setLocked(true);
        setShowThankYou(true);
        setSubmitting(false);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.detail ||
          'Could not submit the form. Please try again or contact support.';
        setError(msg);
        setSubmitting(false);
      });
  };

  return (
    <div style={styles.wrapper}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <a href="https://visibilitylabs.ai" aria-label="Go to visibilitylabs.ai">
            <img
              src="/visibilitylabs.svg"
              alt="Visibility Labs"
              style={styles.logoImg}
            />
          </a>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.mainInner}>
          {loading ? (
            <div style={styles.card}>Loading form…</div>
          ) : (
            <>
              {notFound && (
                <div style={styles.thankYouWrap}>
                  <div style={styles.thankYouIcon}>!</div>
                  <div style={styles.thankYouTitle}>
                    Link not found
                  </div>
                  <div style={styles.thankYouText}>
                    This form link is not valid. Please request a new link from
                    the Visibility Labs team.
                  </div>
                  <div style={styles.thankYouActions}>
                    <a
                      href="https://visibilitylabs.ai"
                      style={styles.thankYouBtnPrimary}
                    >
                      Visit visibilitylabs.ai
                    </a>
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      style={styles.thankYouBtnSecondary}
                    >
                      Contact Support
                    </a>
                  </div>
                </div>
              )}

              {showThankYou && !notFound && (
                <div style={styles.thankYouWrap}>
                  <div style={styles.thankYouIcon}>✓</div>
                  <div style={styles.thankYouTitle}>Thank you!</div>
                  <div style={styles.thankYouText}>
                    {`Thanks for taking the time to review and approve the ${
                      session?.client_name || 'company'
                    } overview.`}
                    <br />
                    We’ve received your feedback and will reach out with any
                    further questions.
                    <br />
                    Full speed ahead!
                  </div>
                </div>
              )}

              {!notFound && error && (
                <div style={styles.errorBanner}>{error}</div>
              )}

              {!notFound && (
                <>
                  <h1 style={styles.title}>
                    {session?.client_name
                      ? `${session.client_name} - Company Overview`
                      : 'Company Overview'}
                  </h1>
                  <p style={styles.subtitle}>
                    {INSTRUCTIONS.split('\n\n').map((p, idx) => (
                      <span key={idx}>
                        {p}
                        {idx === 0 ? <><br /><br /></> : null}
                      </span>
                    ))}
                  </p>

                  <form onSubmit={onSubmit} style={styles.card}>
                {FIELD_ORDER.map((key) => {
                  const value = formData?.[key] ?? '';

                  return (
                    <div key={key} style={styles.row}>
                      <label style={styles.label}>{key}</label>
                      <textarea
                        style={styles.textarea}
                        value={value}
                        disabled={locked}
                        onChange={(e) => onChangeField(key, e.target.value)}
                      />
                    </div>
                  );
                })}

                <div style={styles.row}>
                  <label style={styles.label}>
                    Additional Comments (optional)
                  </label>
                  <textarea
                    style={{ ...styles.textarea, minHeight: '140px' }}
                    value={feedback}
                    disabled={locked}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Tell us what looks good, what’s missing, or what should be updated…"
                  />
                </div>

                <div style={styles.actions}>
                  {!locked && (
                    <button type="submit" style={styles.button}>
                      {submitting ? 'Submitting…' : 'Approve & Submit'}
                    </button>
                  )}
                </div>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </main>

      <footer style={styles.footer}>
        <p style={styles.footerText}>
          Visibility Labs &copy; {new Date().getFullYear()}. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default InitialOnboardingDataApproval;

